import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qInventoryPickerCandidates,
  pickerCandidateRowToSelection,
  // PRD.207 — supplier master data for the PO supplier selector.
  qActiveInventorySuppliers,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
  type InventorySupplierRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { NavIcon, type ShellOutletContext } from '../../shell/shared';
import { InventoryPageShell } from './_shared';

// ─── Local draft ───

interface POLineDraft {
  id: string;
  itemId: string | null;
  itemName: string;
  itemSku: string | null;
  itemUnit: string | null;
  quantityOrdered: string;
  bonusQuantity: string;
  rate: string;
  remarks: string;
}

function emptyLine(): POLineDraft {
  return {
    id: crypto.randomUUID(),
    itemId: null,
    itemName: '',
    itemSku: null,
    itemUnit: null,
    quantityOrdered: '',
    bonusQuantity: '',
    rate: '',
    remarks: '',
  };
}

function lineAmount(line: POLineDraft): number {
  const qty = Number(line.quantityOrdered) || 0;
  const rate = Number(line.rate) || 0;
  const amount = qty * rate;
  return Number.isFinite(amount) ? Math.max(0, amount) : 0;
}

// ─── Page ───

export function CreatePurchaseOrderPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const { db } = usePowerSyncDb();
  const fmt = useFormatMoney();

  const today = new Date().toISOString().slice(0, 10);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState(today);
  // PRD.207 — supplier_id is the FK link to inventory_suppliers; the
  // captured `supplierName` is written to purchase_orders.supplier_name
  // as a snapshot so legacy free-text rendering keeps working.
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState('');
  const [amountsAre, setAmountsAre] = useState<'tax_exclusive' | 'tax_inclusive'>('tax_exclusive');
  const [notes, setNotes] = useState('');

  const { data: supplierOptions = [] } = usePowerSyncQuery<InventorySupplierRow>(
    qActiveInventorySuppliers(),
  );

  const [lines, setLines] = useState<POLineDraft[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function updateLine(id: string, patch: Partial<POLineDraft>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function removeLine(id: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== id)));
  }

  const subtotal = useMemo(() => lines.reduce((sum, l) => sum + lineAmount(l), 0), [lines]);

  const handleCancel = () => navigate('/inventory/purchase-orders');

  const handleSave = async () => {
    setError(null);
    if (!db) return setError('Local database not ready. Please try again in a moment.');
    if (!orgId) return setError('Missing organization context. Please sign in again.');
    if (!supplierName.trim()) return setError('Enter a supplier name.');
    if (!poDate) return setError('PO Date is required.');
    if (lines.length === 0) return setError('Add at least one item.');

    for (const line of lines) {
      if (!line.itemId) return setError('Select an item for every line.');
      const qty = Number(line.quantityOrdered);
      if (!Number.isFinite(qty) || qty <= 0)
        return setError('Enter a quantity greater than zero on every line.');
      const rate = Number(line.rate);
      if (!Number.isFinite(rate) || rate < 0)
        return setError('Enter a non-negative rate on every line.');
    }

    setSaving(true);
    try {
      const poId = crypto.randomUUID();
      const now = new Date().toISOString();
      const number = poNumber.trim() || `PO-${now.slice(0, 10)}-${poId.slice(0, 4)}`;

      // 1) Header — NOTE: no stock_movements written.
      // PRD.207 — `supplier_id` links the PO to inventory_suppliers when
      // the user picked a master record; `supplier_name` is the historical
      // snapshot (still set even when supplier_id is present so legacy
      // free-text rendering keeps working without a join).
      await db.execute(
        `INSERT INTO purchase_orders (
          id, org_id, po_number, po_date, supplier_id, supplier_name,
          amounts_are, notes, status, total_amount, created_by,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          poId,
          orgId,
          number,
          poDate,
          supplierId,
          supplierName.trim(),
          amountsAre,
          notes.trim() || null,
          'posted',
          subtotal,
          userId || null,
          now,
          now,
        ],
      );

      // 2) Lines
      for (const line of lines) {
        const qty = Number(line.quantityOrdered) || 0;
        const bonus = Number(line.bonusQuantity) || 0;
        const rate = Number(line.rate) || 0;
        await db.execute(
          `INSERT INTO purchase_order_lines (
            id, org_id, purchase_order_id, inventory_item_id,
            item_name_snapshot, sku_snapshot, unit_snapshot,
            quantity_ordered, bonus_quantity, rate, amount, remarks,
            created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            line.id,
            orgId,
            poId,
            line.itemId,
            line.itemName,
            line.itemSku,
            line.itemUnit,
            qty,
            bonus,
            rate,
            qty * rate,
            line.remarks.trim() || null,
            now,
            now,
          ],
        );
      }

      navigate('/inventory/purchase-orders');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <InventoryPageShell
      title="Create Purchase Order"
      subtitle="Record an intent to buy from a supplier. No stock is moved until the matching Delivery Order is received."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <>
          <Button variant="outline" size="md" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Purchase Order'}
          </Button>
        </>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {error && (
            <div
              role="alert"
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-3) var(--sp-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}

          <Card title="Purchase Order Details">
            <Grid2>
              <Field label="PO No.">
                <TextInput
                  value={poNumber}
                  onChange={setPoNumber}
                  placeholder="Auto-generated if blank"
                />
              </Field>
              <Field label="Date" required>
                <TextInput type="date" value={poDate} onChange={setPoDate} />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Supplier" required>
                {/* PRD.207 — supplier selector. Picking a master supplier
                    sets supplier_id and snapshots the name. Users without
                    a matching master record can fall back to free text by
                    typing into the input below the dropdown — the selector
                    clears supplier_id when free text is used so the legacy
                    behavior is preserved. */}
                <SupplierSelector
                  options={supplierOptions}
                  supplierId={supplierId}
                  supplierName={supplierName}
                  onSelect={(s) => {
                    setSupplierId(s.id);
                    setSupplierName(s.supplier_name);
                  }}
                  onTypeFreeText={(name) => {
                    setSupplierId(null);
                    setSupplierName(name);
                  }}
                />
              </Field>
              <Field label="Amounts Are">
                <select
                  value={amountsAre}
                  onChange={(e) =>
                    setAmountsAre(e.target.value as 'tax_exclusive' | 'tax_inclusive')
                  }
                  style={{ ...inputStyle, height: 40, cursor: 'pointer' }}
                >
                  <option value="tax_exclusive">Tax Exclusive</option>
                  <option value="tax_inclusive">Tax Inclusive</option>
                </select>
              </Field>
            </Grid2>
            <Field label="Notes">
              <TextInput value={notes} onChange={setNotes} placeholder="Optional PO-level notes" />
            </Field>
          </Card>

          <Card
            title="Items"
            actions={
              <Button
                variant="outline"
                size="md"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                + Add Line
              </Button>
            }
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
                <thead>
                  <tr>
                    <Th>Item</Th>
                    <Th>SKU</Th>
                    <Th>Unit</Th>
                    <Th align="right">Qty Ordered</Th>
                    <Th align="right">Bonus Qty</Th>
                    <Th align="right">Rate</Th>
                    <Th align="right">Amount</Th>
                    <Th>Remarks</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <POLineRow
                      key={line.id}
                      line={line}
                      onChange={(patch) => updateLine(line.id, patch)}
                      onRemove={() => removeLine(line.id)}
                      canRemove={lines.length > 1}
                    />
                  ))}
                  <tr>
                    <td colSpan={6} style={{ padding: '12px 8px', textAlign: 'right' }}>
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Subtotal
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'right',
                        fontWeight: 'var(--font-weight-semi)',
                      }}
                    >
                      {fmt(subtotal)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </InventoryPageShell>
  );
}

// ─── PO Line row ───

function POLineRow({
  line,
  onChange,
  onRemove,
  canRemove,
}: {
  line: POLineDraft;
  onChange: (patch: Partial<POLineDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const fmt = useFormatMoney();
  const tdStyle: CSSProperties = {
    padding: '6px 8px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
  };
  const amount = lineAmount(line);
  return (
    <tr>
      <td style={{ ...tdStyle, minWidth: 220 }}>
        <ItemPicker
          value={line.itemName}
          onSelect={(selection) => {
            onChange({
              itemId: selection.inventoryItemId,
              itemName: selection.itemName,
              itemSku: selection.sku,
              itemUnit: selection.inventoryUnit,
              // Prefill rate from selling price if rate is empty.
              rate:
                line.rate ||
                (selection.sellingPrice != null ? String(selection.sellingPrice) : line.rate),
            });
          }}
        />
      </td>
      <td style={{ ...tdStyle, minWidth: 110 }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.itemSku ?? '—'}
        </span>
      </td>
      <td style={{ ...tdStyle, minWidth: 80 }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.itemUnit ?? '—'}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.quantityOrdered}
          onChange={(e) => onChange({ quantityOrdered: e.target.value })}
          placeholder="0"
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.bonusQuantity}
          onChange={(e) => onChange({ bonusQuantity: e.target.value })}
          placeholder="0"
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.rate}
          onChange={(e) => onChange({ rate: e.target.value })}
          placeholder="0.00"
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {fmt(amount)}
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={line.remarks}
          onChange={(e) => onChange({ remarks: e.target.value })}
          placeholder="Optional"
          style={inputStyle}
        />
      </td>
      <td style={tdStyle}>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Remove line"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            cursor: canRemove ? 'pointer' : 'not-allowed',
            opacity: canRemove ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <NavIcon name="trash" />
        </button>
      </td>
    </tr>
  );
}

// ─── Item Picker (reused inline pattern) ───

function ItemPicker({
  value,
  onSelect,
}: {
  value: string;
  onSelect: (selection: InventoryPickerSelection) => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // PRD.205 — purchase orders accept Medicine and Product items.
  // PRD.264 — Supplies too (incl. non-sellable expendables): procurement is
  // inventory management, so sellability must not gate it. Delivery Orders
  // inherit these lines when the PO is received.
  const { data: candidateRows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    qInventoryPickerCandidates(debounced, ['medicine', 'product', 'supply']),
  );

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Search Medicine items…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {open && candidateRows.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 30,
          }}
        >
          {candidateRows.slice(0, 20).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => {
                  onSelect(selection);
                  setQuery(selection.itemName);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--bg-hover, rgba(0,0,0,0.04))';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  {selection.itemName}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 1,
                  }}
                >
                  {[selection.sku, selection.inventoryUnit && `Unit: ${selection.inventoryUnit}`]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Small primitives (mirrors CreateInventoryAdjustmentPage) ───

function Card({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-family)',
};

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, height: 40 }}
    />
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}

// PRD.207 — Supplier selector for the PO form. Combines a search input
// with a typeahead dropdown of active suppliers. Picking a supplier sets
// `supplier_id` + name snapshot; typing free text clears `supplier_id`
// so legacy behavior remains available for one-off vendors.
function SupplierSelector({
  options,
  supplierId,
  supplierName,
  onSelect,
  onTypeFreeText,
}: {
  options: InventorySupplierRow[];
  supplierId: string | null;
  supplierName: string;
  onSelect: (s: InventorySupplierRow) => void;
  onTypeFreeText: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = supplierName.trim().toLowerCase();
    if (!q) return options.slice(0, 12);
    return options
      .filter((o) => {
        return (
          o.supplier_name.toLowerCase().includes(q) ||
          (o.contact_person ?? '').toLowerCase().includes(q) ||
          (o.phone ?? '').toLowerCase().includes(q) ||
          (o.email ?? '').toLowerCase().includes(q)
        );
      })
      .slice(0, 12);
  }, [options, supplierName]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          value={supplierName}
          onChange={(e) => {
            onTypeFreeText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search supplier or type free text…"
          style={{ ...inputStyle, height: 40, flex: 1 }}
        />
        {supplierId && (
          <span
            title="Linked to a supplier master record"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 6px',
              height: 22,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Linked
          </span>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 30,
          }}
        >
          {filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              onClick={() => {
                onSelect(s);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                background: supplierId === s.id ? 'var(--bg-surface-2)' : 'transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  'var(--bg-hover, rgba(0,0,0,0.04))';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  supplierId === s.id ? 'var(--bg-surface-2)' : 'transparent';
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {s.supplier_name}
              </span>
              {(s.contact_person || s.phone || s.email) && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {[s.contact_person, s.phone, s.email].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
